import React, { useCallback, useEffect, useRef, useState } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { useGeneratePropertySummary } from '~/hooks/apis/property-summary/useGeneratePropertySummary';

import {
  ParityData,
  SalesforceData,
  VnsData,
  parseParityReport,
  parseSalesforceReport,
  parseVnsReport,
} from './parseNextBestActionFiles';

interface Props {
  open: boolean;
  onClose: () => void;
  propertyName: string;
}

type ParseState<T> =
  | { status: 'idle' }
  | { status: 'parsing' }
  | { status: 'matched'; data: T }
  | { status: 'not-found' }
  | { status: 'error'; message: string };

const RESULT_SECTIONS = [
  { key: 'CAM' },
  { key: 'SF Opp' },
  { key: "Yesterday's parity data" },
  { key: 'VNS' },
  { key: 'Recommended next action' },
];

export function parseResultSections(text: string): Array<{ label: string; content: string }> {
  const keys = RESULT_SECTIONS.map((s) => s.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${keys.join('|')}):\\s*`, 'g');

  const parts = text.split(pattern);
  // split with capture group returns: [pre, label1, content1, label2, content2, ...]
  const parsed: Array<{ label: string; content: string }> = [];
  for (let i = 1; i < parts.length; i += 2) {
    const label = parts[i]?.trim();
    const content = parts[i + 1]?.trim().replace(/\s+/g, ' ') ?? '';
    if (label) parsed.push({ label, content });
  }

  return parsed.length > 0 ? parsed : [{ label: '', content: text }];
}

interface UploadAreaProps {
  label: string;
  hint: string;
  file: File | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onChange: (file: File | null) => void;
  accept?: string;
  matchState: ParseState<unknown>;
  matchLabel?: string;
}

function UploadArea({
  label,
  hint,
  file,
  inputRef,
  onChange,
  accept = '.csv',
  matchState,
  matchLabel,
}: UploadAreaProps) {
  const acceptLabel = accept.replace(/\./g, '').toUpperCase();
  return (
    <Box>
      <Box
        onClick={() => inputRef.current?.click()}
        sx={{
          border: '1.5px dashed',
          borderColor: file ? 'success.main' : 'grey.300',
          borderRadius: 1,
          p: 1.5,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: file ? 'success.50' : 'grey.50',
          transition: 'border-color 0.15s, background-color 0.15s',
          '&:hover': {
            borderColor: file ? 'success.dark' : 'primary.main',
            bgcolor: file ? 'success.50' : 'primary.50',
          },
        }}
      >
        {file ? (
          <CheckCircleIcon fontSize="small" color="success" />
        ) : (
          <UploadFileIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" fontWeight={500} color={file ? 'success.dark' : 'text.primary'}>
              {label}
            </Typography>
            <Tooltip title={hint} arrow>
              <InfoOutlinedIcon
                sx={{ fontSize: 14, color: 'text.disabled', cursor: 'default' }}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Stack>
          <Typography
            variant="caption"
            color={file ? 'success.main' : 'text.secondary'}
            sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {file ? file.name : `Click to upload ${acceptLabel}`}
          </Typography>
        </Box>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={(e) => onChange(e.target.files?.[0] ?? null)}
        />
      </Box>
      {matchState.status === 'parsing' && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5, px: 0.5 }}>
          <CircularProgress size={10} />
          <Typography variant="caption" color="text.secondary">
            Matching...
          </Typography>
        </Stack>
      )}
      {matchState.status === 'matched' && (
        <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
          Matched Property: {matchLabel}
        </Typography>
      )}
      {matchState.status === 'not-found' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
          Not found in file
        </Typography>
      )}
      {matchState.status === 'error' && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
          {matchState.message}
        </Typography>
      )}
    </Box>
  );
}

export default function NextBestActionModal({ open, onClose, propertyName }: Props) {
  const [parityReport, setParityReport] = useState<File | null>(null);
  const [salesforceReport, setSalesforceReport] = useState<File | null>(null);
  const [vns, setVns] = useState<File | null>(null);
  const [copied, setCopied] = useState(false);

  const [vnsState, setVnsState] = useState<ParseState<VnsData>>({ status: 'idle' });
  const [sfState, setSfState] = useState<ParseState<SalesforceData>>({ status: 'idle' });
  const [parityState, setParityState] = useState<ParseState<ParityData>>({ status: 'idle' });

  const parityReportRef = useRef<HTMLInputElement>(null);
  const salesforceReportRef = useRef<HTMLInputElement>(null);
  const vnsRef = useRef<HTMLInputElement>(null);

  const { mutate, isPending, isError, error, data, reset } = useGeneratePropertySummary();

  const vnsData = vnsState.status === 'matched' ? vnsState.data : null;
  const canGenerate =
    parityReport !== null && salesforceReport !== null && vns !== null && vnsState.status === 'matched';
  const sections = data ? parseResultSections(data.result) : [];

  const handleVnsChange = useCallback(
    async (file: File | null) => {
      setVns(file);
      if (!file) {
        setVnsState({ status: 'idle' });
        return;
      }
      setVnsState({ status: 'parsing' });
      try {
        const text = await file.text();
        setVnsState({ status: 'matched', data: parseVnsReport(text, propertyName) });
      } catch (err) {
        setVnsState({ status: 'error', message: err instanceof Error ? err.message : 'Failed to parse VNS file.' });
      }
    },
    [propertyName],
  );

  const handleSfChange = useCallback(
    async (file: File | null) => {
      setSalesforceReport(file);
      if (!file) {
        setSfState({ status: 'idle' });
        return;
      }
      setSfState({ status: 'parsing' });
      try {
        const buffer = await file.arrayBuffer();
        const result = parseSalesforceReport(buffer, propertyName);
        setSfState(result ? { status: 'matched', data: result } : { status: 'not-found' });
      } catch (err) {
        setSfState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed to parse Salesforce file.',
        });
      }
    },
    [propertyName],
  );

  const handleParityChange = useCallback((file: File | null) => {
    setParityReport(file);
    if (!file) setParityState({ status: 'idle' });
  }, []);

  useEffect(() => {
    if (!parityReport || !vnsData) {
      if (parityReport) setParityState({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setParityState({ status: 'parsing' });
    parseParityReport(parityReport, vnsData.offerId)
      .then((result) => {
        if (!cancelled) setParityState(result ? { status: 'matched', data: result } : { status: 'not-found' });
      })
      .catch((err) => {
        if (!cancelled)
          setParityState({
            status: 'error',
            message: err instanceof Error ? err.message : 'Failed to parse parity report.',
          });
      });
    return () => {
      cancelled = true;
    };
  }, [parityReport, vnsData]);

  const handleClose = useCallback(() => {
    setParityReport(null);
    setSalesforceReport(null);
    setVns(null);
    setVnsState({ status: 'idle' });
    setSfState({ status: 'idle' });
    setParityState({ status: 'idle' });
    setCopied(false);
    if (parityReportRef.current) parityReportRef.current.value = '';
    if (salesforceReportRef.current) salesforceReportRef.current.value = '';
    if (vnsRef.current) vnsRef.current.value = '';
    reset();
    onClose();
  }, [reset, onClose]);

  const handleGenerate = useCallback(() => {
    if (!vnsData) return;
    reset();
    const { matchedName: _matchedName, ...vnsPayload } = vnsData;
    mutate({
      propertyName,
      vns: vnsPayload,
      ...(parityState.status === 'matched' ? { parity: parityState.data } : {}),
      ...(sfState.status === 'matched' ? { salesforce: sfState.data } : {}),
    });
  }, [vnsData, parityState, sfState, propertyName, mutate, reset]);

  const handleCopy = useCallback(() => {
    if (!data) return;
    navigator.clipboard.writeText(data.result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [data]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={700} component="span">
          Next Best Action
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {propertyName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            <UploadArea
              label="Price Parity Report (.xlsx)"
              hint='Excel file (.xlsx) with an "LPC Exec" sheet containing Offer ID, Views, and parity columns.'
              file={parityReport}
              inputRef={parityReportRef}
              onChange={handleParityChange}
              accept=".xlsx"
              matchState={parityState}
              matchLabel={propertyName}
            />
            <UploadArea
              label="Salesforce Rate Parity Report"
              hint='CSV file with an "Opportunity Name" column matching the property name.'
              file={salesforceReport}
              inputRef={salesforceReportRef}
              onChange={handleSfChange}
              matchState={sfState}
              matchLabel={sfState.status === 'matched' ? sfState.data.opportunityName : undefined}
            />
            <UploadArea
              label="VNS Accommodation Overview"
              hint='CSV file with "Property Name" and "offer_id" columns matching this property.'
              file={vns}
              inputRef={vnsRef}
              onChange={handleVnsChange}
              matchState={vnsState}
              matchLabel={vnsState.status === 'matched' ? vnsState.data.matchedName : undefined}
            />
          </Box>

          {isError && (
            <Alert severity="error">
              {error instanceof Error ? error.message : 'Failed to generate summary. Please try again.'}
            </Alert>
          )}

          {data && sections.length > 0 && (
            <>
              <Divider />
              <Stack spacing={0.75}>
                {sections.map((section, i) => {
                  const isRecommendation = section.label === 'Recommended next action';
                  return (
                    <Box
                      key={section.label || i}
                      sx={{
                        border: '1px solid',
                        borderColor: isRecommendation ? 'primary.main' : 'grey.200',
                        borderLeft: '3px solid',
                        borderLeftColor: isRecommendation ? 'primary.main' : 'grey.300',
                        borderRadius: '0 4px 4px 0',
                        bgcolor: isRecommendation ? 'primary.50' : 'common.white',
                        px: 1.5,
                        py: 1,
                      }}
                    >
                      {section.label && (
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color={isRecommendation ? 'primary.main' : 'text.secondary'}
                          sx={{ display: 'block', mb: 0.25 }}
                        >
                          {section.label.toUpperCase()}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.primary" fontWeight={isRecommendation ? 500 : 400}>
                        {section.content}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={handleClose}>Close</Button>
        <Stack direction="row" spacing={1}>
          {data && (
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <Button
                startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={handleCopy}
                color={copied ? 'success' : 'primary'}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </Tooltip>
          )}
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={!canGenerate || isPending}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isPending ? 'Generating...' : 'Generate'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
